import { useState } from 'react';
import { Alert, Button, Card, Group, Loader, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSend } from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';

/**
 * Step 2: prove the connection actually carries messages.
 *
 * This step does not exist today, and its absence is why onboarding has no
 * ending. A saved token proves nothing — the vendor learns whether messages
 * really flow when a customer messages them and either does or doesn't get a
 * reply. Here we compress that into thirty seconds: send yourself a template,
 * reply from your phone, watch the card flip.
 *
 * This card only ever renders the *unproven* half of the story. The moment
 * `hasReceivedFirstMessage` flips, the state machine moves the corridor to LIVE
 * and the success screen takes over — so a "you're connected" branch in here
 * would be unreachable. Polling lives in the parent; there is one poller.
 *
 * `hello_world` is used because templates deliver outside the 24-hour customer
 * service window — it is the only send that works before anyone has messaged
 * the vendor, which is precisely the situation a new account is in.
 */
export function VerifyLiveCard() {
  const [to, setTo] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendTest = trpc.integrations.meta.sendWhatsAppTestMessage.useMutation({
    onSuccess: () => {
      setSent(true);
      setError(null);
    },
    onError: (err) => {
      setError(err.message);
      notifications.show({ message: err.message, color: 'red' });
    },
  });

  // Mirror the server's E.164 rule so the error is instant rather than a
  // round-trip. The server validates again regardless.
  const trimmed = to.trim();
  const looksValid = /^\+?[1-9]\d{6,14}$/.test(trimmed);

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack gap="md">
        <Stack gap={4}>
          <Text fw={600}>Let&apos;s make sure it works</Text>
          <Text size="sm" c="dimmed">
            Send yourself a test message, then reply to it from your phone. That proves messages
            travel both ways.
          </Text>
        </Stack>

        <Group align="flex-start" gap="sm" wrap="nowrap">
          <TextInput
            flex={1}
            label="Your WhatsApp number"
            description="Include the country code."
            placeholder="+91 98765 43210"
            value={to}
            onChange={(e) => setTo(e.currentTarget.value)}
            error={trimmed.length > 0 && !looksValid ? 'Include the country code, e.g. +91…' : null}
          />
          <Button
            mt={28}
            leftSection={<IconSend size={16} />}
            disabled={!looksValid}
            loading={sendTest.isPending}
            onClick={() => sendTest.mutate({ to: trimmed })}
          >
            Send test
          </Button>
        </Group>

        {error && (
          <Alert color="red" variant="light" title="That send didn't go through">
            <Text size="sm">{error}</Text>
          </Alert>
        )}

        {sent && !error && (
          <Alert color="blue" variant="light">
            <Group gap="sm" wrap="nowrap">
              <Loader size="xs" />
              <Stack gap={2}>
                <Text size="sm" fw={500}>
                  Sent. Now reply to it from your phone.
                </Text>
                <Text size="xs" c="dimmed">
                  Waiting for your reply — this page updates on its own.
                </Text>
              </Stack>
            </Group>
          </Alert>
        )}
      </Stack>
    </Card>
  );
}
